/**
 * v1.3.7 — Banner che rileva quando il bundle JS in esecuzione è OBSOLETO rispetto al
 * backend e propone all'utente di fare un "hard refresh" con un click.
 *
 * Strategia:
 *   - Al mount + ogni 60s fa GET /api/system/version (no-cache).
 *   - Se la versione live del backend è DIVERSA dalla APP_VERSION del bundle locale,
 *     mostra un banner sticky in alto con il bottone "Aggiorna ora".
 *   - Aggiorna ora: svuota localStorage/sessionStorage (mantenendo token), deregistra
 *     Service Worker e fa window.location.reload(true).
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, RefreshCcw, X } from 'lucide-react';
import { APP_VERSION } from '../version';

const BANNER_STORAGE_KEY = 'cen_dismissed_version_banner_for';

export default function VersionMismatchBanner() {
  const [serverVersion, setServerVersion] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchVersion = async () => {
      try {
        const base = process.env.REACT_APP_BACKEND_URL || '';
        const res = await fetch(`${base}/api/system/version?_t=${Date.now()}`, {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache, no-store' },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data?.version) {
          setServerVersion(data.version);
          // Se l'utente aveva già dismesso il banner per QUESTA versione, mantieni dismissed
          const dismissedFor = localStorage.getItem(BANNER_STORAGE_KEY);
          setDismissed(dismissedFor === data.version);
        }
      } catch { /* offline o backend giù → nessun banner */ }
    };
    fetchVersion();
    const id = setInterval(fetchVersion, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const mismatch = serverVersion && serverVersion !== APP_VERSION;

  const handleUpgrade = () => {
    // v1.3.7 — FIX (testing agent iter46): il token è in sessionStorage, NON localStorage.
    // Preservo entrambi per essere a prova di futuri cambi di storage.
    const lsToken = localStorage.getItem('token');
    const ssToken = sessionStorage.getItem('token');
    try { localStorage.clear(); } catch { /* ignore */ }
    try { sessionStorage.clear(); } catch { /* ignore */ }
    if (lsToken) localStorage.setItem('token', lsToken);
    if (ssToken) sessionStorage.setItem('token', ssToken);
    // 3. Deregistra Service Worker se presente
    try {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(regs => regs.forEach(r => r.unregister()));
      }
    } catch { /* ignore */ }
    // 4. Forza reload bypassando la cache (Cache-Control: no-cache via query param)
    const url = new URL(window.location.href);
    url.searchParams.set('_cb', Date.now().toString());
    window.location.replace(url.toString());
  };

  const handleDismiss = () => {
    if (serverVersion) localStorage.setItem(BANNER_STORAGE_KEY, serverVersion);
    setDismissed(true);
  };

  if (!mismatch || dismissed) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[60] bg-amber-500 text-amber-950 shadow-lg border-b-2 border-amber-700"
      data-testid="version-mismatch-banner"
    >
      <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center gap-3">
        <AlertTriangle className="w-5 h-5 flex-shrink-0" />
        <div className="flex-1 text-sm">
          <strong>Nuova versione disponibile</strong> ({serverVersion}). Stai usando una versione vecchia ({APP_VERSION}).
          <span className="hidden sm:inline"> Aggiorna per evitare malfunzionamenti.</span>
        </div>
        <button
          onClick={handleUpgrade}
          className="bg-amber-900 hover:bg-amber-950 text-white text-sm font-semibold px-3 py-1.5 rounded inline-flex items-center gap-1.5"
          data-testid="version-upgrade-btn"
        >
          <RefreshCcw className="w-3.5 h-3.5" /> Aggiorna ora
        </button>
        <button
          onClick={handleDismiss}
          className="text-amber-900 hover:text-amber-700 p-1"
          aria-label="Chiudi"
          data-testid="version-dismiss-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
